import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../config/api'
import { getToken, saveToken, clearToken, tokenExpiry, isExpired, isSessionFailure, SESSION_EXPIRED_EVENT } from '../utils/session'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

const AUTH = `${API_BASE_URL}/api/customers`
const SESSION_ENDED = 'Your session has ended. Please sign in again.'
// Longest delay setTimeout accepts; later expiries are re-checked when the page is next opened
const MAX_TIMER_MS = 2 ** 31 - 1

/** The server's explanation (lockout minutes, sign-up conflict) before its short error code */
const serverMessage = (error, fallback) => {
  const data = error?.response?.data
  if (data?.message || data?.error) return data.message || data.error
  if (error?.response?.status === 429) return 'Too many attempts. Please wait a few minutes and try again.'
  return error?.response ? fallback : 'Cannot reach the server. Check your connection and try again.'
}

// Other screens' axios calls (AI chat) rely on the default Authorization header
function setAxiosToken(token) {
  const common = axios.defaults?.headers?.common
  if (!common) return
  if (token) common.Authorization = `Bearer ${token}`
  else delete common.Authorization
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(() => getToken())
  const [sessionNotice, setSessionNotice] = useState(null)   // shown on the login screen after a forced sign-out

  useEffect(() => { setAxiosToken(token) }, [token])

  const endSession = useCallback((notice = null) => {
    setUser(null)
    setToken(null)
    clearToken()
    setAxiosToken(null)
    setSessionNotice(notice)
  }, [])

  const startSession = useCallback((customer, newToken) => {
    saveToken(newToken)
    setAxiosToken(newToken)
    setToken(newToken)
    setUser(customer)
    setSessionNotice(null)
  }, [])

  // Restore the session on app start: a stored, unexpired token is checked against the server
  useEffect(() => {
    const savedToken = getToken()
    if (!savedToken) { setLoading(false); return }
    if (isExpired(savedToken)) { endSession(SESSION_ENDED); setLoading(false); return }
    let alive = true
    axios.get(`${AUTH}/profile`, { headers: { Authorization: `Bearer ${savedToken}` }, timeout: 10000 })
      .then(response => { if (alive) setUser(response.data) })
      .catch(error => {
        if (!alive) return
        // Only a rejected token ends the session; a network blip keeps it for the next try
        if (isSessionFailure(error?.response?.status, error?.response?.data, savedToken)) endSession(SESSION_ENDED)
        else console.error('Session check failed:', error?.message)
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [endSession])

  // Sign out when the token's lifetime (set by the server) runs out
  useEffect(() => {
    const exp = token && tokenExpiry(token)
    if (!exp) return
    const timer = setTimeout(() => endSession(SESSION_ENDED), Math.min(Math.max(exp - Date.now(), 0), MAX_TIMER_MS))
    return () => clearTimeout(timer)
  }, [token, endSession])

  // Sign out when any API call reports the token as rejected (fetch helpers dispatch the event; axios via interceptor)
  useEffect(() => {
    const onExpired = () => endSession(SESSION_ENDED)
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    const interceptors = axios.interceptors?.response
    const id = interceptors?.use(r => r, error => {
      if (isSessionFailure(error?.response?.status, error?.response?.data)) onExpired()
      return Promise.reject(error)
    })
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
      if (id !== undefined) interceptors.eject(id)
    }
  }, [endSession])

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${AUTH}/login`, { email, password })
      const { customer, token: newToken } = response.data
      startSession(customer, newToken)
      return { success: true }
    } catch (error) {
      return { success: false, error: serverMessage(error, 'Sign-in failed. Please try again.') }
    }
  }

  const register = async (userData) => {
    try {
      const response = await axios.post(`${AUTH}/register`, userData)
      const { customer, token: newToken } = response.data
      startSession(customer, newToken)
      return { success: true }
    } catch (error) {
      // code SIGNUP_CONFLICT: the screen offers the WhatsApp-code sign-in (the answer never says which field is taken)
      return { success: false, error: serverMessage(error, 'Registration failed. Please try again.'), code: error?.response?.data?.code }
    }
  }

  // ---- Account recovery (backend routes/customers.js). Answers never say whether an account exists. ----
  const failure = (error, fallback) => ({
    success: false, error: serverMessage(error, fallback), code: error?.response?.data?.code, status: error?.response?.status
  })

  /** Email a reset link (same answer whether or not the email has an account) */
  const requestPasswordReset = async (email) => {
    try {
      const { data } = await axios.post(`${AUTH}/forgot-password`, { email })
      return { success: true, message: data.message }
    } catch (error) {
      return failure(error, 'Could not send the link. Please try again.')
    }
  }

  /** Is this reset link still usable? { valid, code, error } */
  const checkResetLink = async (token) => {
    try {
      const { data } = await axios.post(`${AUTH}/reset-password/check`, { token })
      return { success: true, ...data }
    } catch (error) {
      return failure(error, 'Could not check the link. Please try again.')
    }
  }

  /** New password from an email link; every signed-in session (this one too) ends */
  const resetPassword = async (token, password) => {
    try {
      const { data } = await axios.post(`${AUTH}/reset-password`, { token, password })
      endSession(null)
      return { success: true, message: data.message }
    } catch (error) {
      return failure(error, 'Could not reset the password. Please try again.')
    }
  }

  /** A sign-in code on WhatsApp. reachable=false: they must message us first (whatsappNumber) */
  const requestWhatsAppCode = async (phone) => {
    try {
      const { data } = await axios.post(`${AUTH}/login/whatsapp/request`, { phone })
      return { success: true, ...data }
    } catch (error) {
      return failure(error, 'Could not send a code. Please try again.')
    }
  }

  /** Check the code; signs in. Returns hasPassword / hasEmail for the optional "Set a password" step */
  const verifyWhatsAppCode = async (phone, code) => {
    try {
      const { data } = await axios.post(`${AUTH}/login/whatsapp/verify`, { phone, code })
      startSession(data.customer, data.token)
      return { success: true, hasPassword: data.hasPassword, hasEmail: data.hasEmail, setPasswordMinutes: data.setPasswordMinutes }
    } catch (error) {
      return failure(error, 'Sign-in failed. Please try again.')
    }
  }

  /** Set a password (and the sign-in email if there is none); the server answers with a fresh session */
  const setPassword = async ({ password, email, currentPassword }) => {
    try {
      const { data } = await axios.post(`${AUTH}/password`, { password, email, currentPassword })
      startSession(data.customer, data.token)
      return { success: true, message: data.message }
    } catch (error) {
      return failure(error, 'Could not set the password. Please try again.')
    }
  }

  const logout = () => endSession(null)

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.patch(`${AUTH}/profile`, profileData)
      setUser(response.data)
      return { success: true }
    } catch (error) {
      return { success: false, error: serverMessage(error, 'Profile update failed') }
    }
  }

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    requestPasswordReset,
    checkResetLink,
    resetPassword,
    requestWhatsAppCode,
    verifyWhatsAppCode,
    setPassword,
    sessionNotice,
    setSessionNotice,
    clearSessionNotice: () => setSessionNotice(null)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
